/**
 * PublisherAdmin — master dashboard for SocialPulse Publisher.
 *
 * Access: any user with is_admin=true OR platform_role in
 * {super_admin, editor, organization_admin}. Non-admins see a soft
 * "You don't have access" message.
 *
 * Features:
 *   • Filter by status (all / draft / published / scheduled / archived)
 *   • Search by title (client-side, since the API returns everything)
 *   • Quick actions per row: Edit, Publish, Add to site (distribute)
 *   • Row shows author, format (article/note), primary category+location
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, Search, PenLine, Send, ExternalLink, Radio,
  CheckCircle2, XCircle, Filter, Shield, Rocket, StickyNote,
} from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;
const CANONICAL_HOST = "https://blogs.socialpulse.com";

const SITE_OPTIONS = [
  "newspulse", "farmerspulse", "foodpulse", "homespulse",
  "airlinespulse", "worldpulse", "socialpulse",
];
const STATUS_TABS = [
  { key: "all",       label: "All" },
  { key: "draft",     label: "Drafts" },
  { key: "published", label: "Published" },
  { key: "scheduled", label: "Scheduled" },
  { key: "archived",  label: "Archived" },
];

function getToken() {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem("sp_token") || window.localStorage.getItem("token");
}

async function api(path, opts = {}) {
  const token = getToken();
  const r = await fetch(`${BASE}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  if (!r.ok) {
    const detail = await r.text().catch(() => "");
    throw new Error(`HTTP ${r.status}: ${detail}`);
  }
  return r.json();
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return "—";
  }
}

function StatusBadge({ status }) {
  const map = {
    published: "bg-emerald-100 text-emerald-800",
    draft:     "bg-amber-100 text-amber-800",
    scheduled: "bg-blue-100 text-blue-800",
    archived:  "bg-slate-200 text-slate-700",
    removed:   "bg-red-100 text-red-800",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold ${
        map[status] || "bg-slate-100 text-slate-700"
      }`}
      data-testid={`publisher-admin-status-${status}`}
    >
      {status}
    </span>
  );
}

export default function PublisherAdmin() {
  const [articles, setArticles] = useState(null); // null=loading, []=empty, [...]=ready
  const [error, setError] = useState(null);
  const [statusTab, setStatusTab] = useState("all");
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [distTarget, setDistTarget] = useState(null); // article id being distributed
  const [distSelection, setDistSelection] = useState([]); // site_ids picked for distTarget
  const [flash, setFlash] = useState(null);

  // Bulk-blast state: which article ids are checked + which sites the
  // whole batch should go to.
  const [bulkIds, setBulkIds] = useState(new Set());
  const [bulkSites, setBulkSites] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      const rows = await api("/articles?limit=200");
      setArticles(Array.isArray(rows) ? rows : []);
    } catch (e) {
      const msg = e.message || "load_failed";
      setError(msg);
      if (msg.includes("401")) setArticles([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!articles) return [];
    return articles.filter((a) => {
      if (statusTab !== "all" && a.status !== statusTab) return false;
      if (!query.trim()) return true;
      const q = query.trim().toLowerCase();
      return (
        (a.title || "").toLowerCase().includes(q) ||
        (a.author_id || "").toLowerCase().includes(q) ||
        (a.primary_category || "").toLowerCase().includes(q) ||
        (a.primary_location_id || "").toLowerCase().includes(q)
      );
    });
  }, [articles, statusTab, query]);

  const counts = useMemo(() => {
    const c = { all: 0, draft: 0, published: 0, scheduled: 0, archived: 0 };
    (articles || []).forEach((a) => {
      c.all += 1;
      if (c[a.status] !== undefined) c[a.status] += 1;
    });
    return c;
  }, [articles]);

  const doPublish = async (id) => {
    setBusyId(id);
    try {
      await api(`/articles/${id}/publish`, { method: "POST" });
      setFlash({ kind: "ok", msg: "Published" });
      await load();
    } catch (e) {
      setFlash({ kind: "err", msg: e.message || "publish_failed" });
    } finally {
      setBusyId(null);
      setTimeout(() => setFlash(null), 2200);
    }
  };

  const doArchive = async (id) => {
    setBusyId(id);
    try {
      await api(`/articles/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: "archived" }),
      });
      setFlash({ kind: "ok", msg: "Archived" });
      await load();
    } catch (e) {
      setFlash({ kind: "err", msg: e.message || "archive_failed" });
    } finally {
      setBusyId(null);
      setTimeout(() => setFlash(null), 2200);
    }
  };

  const doDistributeMany = async (article, siteIds) => {
    if (!siteIds.length) return;
    setBusyId(article.id);
    const results = { ok: 0, dup: 0, err: 0 };
    for (const site_id of siteIds) {
      try {
        const r = await api(`/articles/${article.id}/distribute`, {
          method: "POST",
          body: JSON.stringify({
            site_id,
            location_id: article.primary_location_id || null,
            category_id: article.primary_category || null,
            policy: "excerpt",
            status: "allowed",
          }),
        });
        if (r?.created) results.ok += 1;
        else if (r?.reason === "duplicate") results.dup += 1;
      } catch (e) {
        results.err += 1;
      }
    }
    const parts = [];
    if (results.ok)  parts.push(`${results.ok} new`);
    if (results.dup) parts.push(`${results.dup} already existed`);
    if (results.err) parts.push(`${results.err} failed`);
    setFlash({
      kind: results.err > 0 && results.ok === 0 ? "err" : "ok",
      msg: `Distributed to ${siteIds.length} site${siteIds.length > 1 ? "s" : ""}: ${parts.join(", ") || "no change"}`,
    });
    setBusyId(null);
    setDistTarget(null);
    setDistSelection([]);
    setTimeout(() => setFlash(null), 2600);
  };

  const toggleBulk = (id) => {
    setBulkIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const clearBulk = () => {
    setBulkIds(new Set());
    setBulkSites([]);
  };

  const doBulkBlast = async () => {
    if (bulkIds.size === 0 || bulkSites.length === 0) return;
    setBulkBusy(true);
    const idsArr = Array.from(bulkIds);
    const results = { ok: 0, dup: 0, err: 0 };
    // Use the loaded article records so we preserve their location + category.
    const byId = new Map((articles || []).map((a) => [a.id, a]));
    for (const id of idsArr) {
      const article = byId.get(id);
      if (!article) { results.err += 1; continue; }
      for (const site_id of bulkSites) {
        try {
          const r = await api(`/articles/${id}/distribute`, {
            method: "POST",
            body: JSON.stringify({
              site_id,
              location_id: article.primary_location_id || null,
              category_id: article.primary_category || null,
              policy: "excerpt",
              status: "allowed",
            }),
          });
          if (r?.created) results.ok += 1;
          else if (r?.reason === "duplicate") results.dup += 1;
        } catch {
          results.err += 1;
        }
      }
    }
    const parts = [];
    if (results.ok)  parts.push(`${results.ok} new`);
    if (results.dup) parts.push(`${results.dup} already existed`);
    if (results.err) parts.push(`${results.err} failed`);
    setFlash({
      kind: results.err > 0 && results.ok === 0 ? "err" : "ok",
      msg: `${idsArr.length} article${idsArr.length > 1 ? "s" : ""} × ${bulkSites.length} site${bulkSites.length > 1 ? "s" : ""}: ${parts.join(", ") || "no change"}`,
    });
    setBulkBusy(false);
    clearBulk();
    setTimeout(() => setFlash(null), 3000);
  };

  if (articles === null && !error) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-24 text-center flex items-center justify-center gap-2 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading Publisher admin…
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <Shield className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Master admin access required</h1>
        <p className="text-slate-600 text-sm mb-4" data-testid="publisher-admin-error">
          {error.includes("401") ? "Please sign in with an admin account." : error}
        </p>
        <Link
          to="/publisher/write"
          className="text-blue-700 underline text-sm"
          data-testid="publisher-admin-write-link"
        >
          Go to Writer
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-6" data-testid="publisher-admin-page">
      {/* Header */}
      <header className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold flex items-center gap-1.5">
            <Rocket className="w-3.5 h-3.5" /> SocialPulse Publisher · master admin
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 mt-1">
            All articles across every author and site
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Publish, archive, and route any post to any site — no need to be the author.
          </p>
        </div>
        <Link
          to="/publisher/write"
          className="inline-flex items-center gap-1 rounded-full bg-slate-900 hover:bg-slate-700 text-white px-4 py-2 text-sm font-semibold"
          data-testid="publisher-admin-new-article"
        >
          <PenLine className="w-4 h-4" /> New article
        </Link>
      </header>

      {/* Quick nav — Notes timeline */}
      <div className="flex items-center gap-3 mb-4 text-xs">
        <Link
          to="/publisher/notes"
          className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-800 font-semibold px-3 py-1.5"
          data-testid="publisher-admin-notes-link"
        >
          <StickyNote className="w-3.5 h-3.5" /> Notes timeline
        </Link>
      </div>

      {/* Status filter tabs */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <Filter className="w-4 h-4 text-slate-400" />
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setStatusTab(t.key)}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors border ${
              statusTab === t.key
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
            }`}
            data-testid={`publisher-admin-tab-${t.key}`}
          >
            {t.label}
            <span className="ml-1.5 text-[10px] opacity-70">{counts[t.key] || 0}</span>
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search title, author, category…"
            className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 outline-none focus:border-blue-400 w-72"
            data-testid="publisher-admin-search"
          />
        </div>
      </div>

      {flash && (
        <div
          className={`mb-3 text-sm rounded-lg px-3 py-2 border ${
            flash.kind === "ok"
              ? "text-emerald-800 bg-emerald-50 border-emerald-200"
              : "text-red-800 bg-red-50 border-red-200"
          }`}
          role="status"
          data-testid="publisher-admin-flash"
        >
          {flash.kind === "ok" ? (
            <CheckCircle2 className="w-4 h-4 inline -mt-0.5 mr-1" />
          ) : (
            <XCircle className="w-4 h-4 inline -mt-0.5 mr-1" />
          )}
          {flash.msg}
        </div>
      )}

      {/* Bulk-blast toolbar — sticky, visible only when the user has
          checked at least one row. Lets Tom fan the whole batch out
          to N sites with one Distribute button. */}
      {bulkIds.size > 0 && (
        <div
          className="sticky top-2 z-30 mb-3 rounded-2xl border border-slate-900 bg-slate-900 text-white px-4 py-3 shadow-lg"
          data-testid="publisher-admin-bulk-toolbar"
        >
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1 text-sm font-semibold">
              <CheckCircle2 className="w-4 h-4 text-emerald-300" />
              <span data-testid="publisher-admin-bulk-count">{bulkIds.size}</span>
              &nbsp;selected
            </span>
            <button
              type="button"
              onClick={clearBulk}
              className="text-xs text-slate-300 hover:text-white underline underline-offset-2"
              data-testid="publisher-admin-bulk-clear"
            >
              Clear
            </button>
            <div className="mx-2 h-4 w-px bg-slate-700" />
            <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
              Blast to
            </span>
            {SITE_OPTIONS.map((s) => {
              const active = bulkSites.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() =>
                    setBulkSites((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]))
                  }
                  disabled={bulkBusy}
                  className={`rounded-full text-[10px] font-semibold uppercase tracking-widest px-2.5 py-1 border transition-colors disabled:opacity-40 ${
                    active
                      ? "bg-emerald-500 text-white border-emerald-500"
                      : "bg-transparent text-slate-200 border-slate-700 hover:border-slate-400"
                  }`}
                  data-testid={`publisher-admin-bulk-site-${s}`}
                  aria-pressed={active}
                >
                  {s}
                </button>
              );
            })}
            <button
              type="button"
              onClick={doBulkBlast}
              disabled={bulkBusy || bulkSites.length === 0 || bulkIds.size === 0}
              className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-600 disabled:cursor-not-allowed text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5"
              data-testid="publisher-admin-bulk-blast"
            >
              {bulkBusy ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Radio className="w-3.5 h-3.5" />
              )}
              Blast {bulkIds.size} × {bulkSites.length || "?"} sites
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div
        className="bg-white border border-slate-200 rounded-2xl overflow-hidden"
        data-testid="publisher-admin-table-wrap"
      >
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-500 italic" data-testid="publisher-admin-empty">
            No articles here yet.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="publisher-admin-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              <tr>
                <th className="w-8 px-3 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && filtered.every((a) => bulkIds.has(a.id))}
                    onChange={(e) => {
                      const next = new Set(bulkIds);
                      if (e.target.checked) filtered.forEach((a) => next.add(a.id));
                      else filtered.forEach((a) => next.delete(a.id));
                      setBulkIds(next);
                    }}
                    className="w-3.5 h-3.5 accent-slate-900 cursor-pointer"
                    aria-label="Select all"
                    data-testid="publisher-admin-select-all"
                  />
                </th>
                <th className="text-left px-4 py-2">Article</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Author</th>
                <th className="text-left px-3 py-2">Where</th>
                <th className="text-left px-3 py-2">Updated</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((a) => (
                <tr key={a.id} data-testid={`publisher-admin-row-${a.id}`} className={`hover:bg-slate-50 ${bulkIds.has(a.id) ? "bg-blue-50/40" : ""}`}>
                  <td className="w-8 px-3 py-3 text-center align-top">
                    <input
                      type="checkbox"
                      checked={bulkIds.has(a.id)}
                      onChange={() => toggleBulk(a.id)}
                      className="w-3.5 h-3.5 accent-slate-900 cursor-pointer"
                      aria-label={`Select ${a.title || a.id}`}
                      data-testid={`publisher-admin-select-${a.id}`}
                    />
                  </td>
                  <td className="px-4 py-3 max-w-md">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
                      {a.content_format === "note" ? (
                        <span className="text-amber-700">NOTE</span>
                      ) : a.content_format === "podcast" ? (
                        <span className="text-purple-700">PODCAST</span>
                      ) : (
                        <span className="text-blue-700">ARTICLE</span>
                      )}
                      <span>·</span>
                      <span>{a.primary_category || "—"}</span>
                    </div>
                    <div
                      className="font-semibold text-slate-900 leading-snug mt-0.5 line-clamp-2"
                      data-testid={`publisher-admin-title-${a.id}`}
                    >
                      {a.title || "(untitled)"}
                    </div>
                    {a.summary && (
                      <div className="text-xs text-slate-500 line-clamp-1 mt-0.5">
                        {a.summary}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3"><StatusBadge status={a.status} /></td>
                  <td className="px-3 py-3 text-xs text-slate-600 font-mono max-w-[140px] truncate" title={a.author_id}>
                    {a.author_id}
                  </td>
                  <td className="px-3 py-3 text-xs text-slate-600">
                    {a.primary_location_id ? (
                      <span className="font-mono">{a.primary_location_id}</span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-xs text-slate-500 whitespace-nowrap">
                    {fmtDate(a.updated_at || a.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Link
                        to={`/publisher/write/${a.id}`}
                        className="rounded-full border border-slate-200 hover:border-slate-400 p-1.5"
                        title="Edit"
                        data-testid={`publisher-admin-edit-${a.id}`}
                      >
                        <PenLine className="w-3.5 h-3.5 text-slate-700" />
                      </Link>
                      {a.status !== "published" && (
                        <button
                          type="button"
                          onClick={() => doPublish(a.id)}
                          disabled={busyId === a.id}
                          className="rounded-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 p-1.5"
                          title="Publish"
                          data-testid={`publisher-admin-publish-${a.id}`}
                        >
                          {busyId === a.id ? (
                            <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
                          ) : (
                            <Send className="w-3.5 h-3.5 text-white" />
                          )}
                        </button>
                      )}
                      {a.status === "published" && (
                        <>
                          <a
                            href={`${CANONICAL_HOST}/article/${a.slug}`}
                            target="_blank"
                            rel="noopener"
                            className="rounded-full border border-slate-200 hover:border-slate-400 p-1.5"
                            title="Open canonical"
                            data-testid={`publisher-admin-open-${a.id}`}
                          >
                            <ExternalLink className="w-3.5 h-3.5 text-slate-700" />
                          </a>
                          <button
                            type="button"
                            onClick={() => {
                              if (distTarget === a.id) {
                                setDistTarget(null);
                                setDistSelection([]);
                              } else {
                                setDistTarget(a.id);
                                setDistSelection([]);
                              }
                            }}
                            className="rounded-full bg-slate-900 hover:bg-slate-700 p-1.5"
                            title="Distribute to sites"
                            data-testid={`publisher-admin-dist-${a.id}`}
                          >
                            <Radio className="w-3.5 h-3.5 text-white" />
                          </button>
                          <button
                            type="button"
                            onClick={() => doArchive(a.id)}
                            disabled={busyId === a.id}
                            className="rounded-full border border-slate-200 hover:border-red-300 p-1.5 text-red-700 disabled:opacity-40"
                            title="Archive"
                            data-testid={`publisher-admin-archive-${a.id}`}
                          >
                            <XCircle className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                    {distTarget === a.id && (
                      <div
                        className="mt-2 flex flex-col items-end gap-2"
                        data-testid={`publisher-admin-dist-form-${a.id}`}
                      >
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                          Blast to sites (multi-select)
                        </div>
                        <div className="flex items-center gap-1 flex-wrap justify-end">
                          {SITE_OPTIONS.map((s) => {
                            const active = distSelection.includes(s);
                            return (
                              <button
                                key={s}
                                type="button"
                                onClick={() =>
                                  setDistSelection((prev) =>
                                    prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
                                  )
                                }
                                disabled={busyId === a.id}
                                className={`rounded-full text-[10px] font-semibold uppercase tracking-widest px-2 py-1 border transition-colors disabled:opacity-40 ${
                                  active
                                    ? "bg-slate-900 text-white border-slate-900"
                                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-500"
                                }`}
                                data-testid={`publisher-admin-dist-${a.id}-${s}`}
                                aria-pressed={active}
                              >
                                {s}
                              </button>
                            );
                          })}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setDistTarget(null);
                              setDistSelection([]);
                            }}
                            className="text-xs text-slate-500 hover:text-slate-800"
                            data-testid={`publisher-admin-dist-cancel-${a.id}`}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => doDistributeMany(a, distSelection)}
                            disabled={busyId === a.id || distSelection.length === 0}
                            className="inline-flex items-center gap-1 rounded-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white text-xs font-semibold px-3 py-1.5"
                            data-testid={`publisher-admin-dist-blast-${a.id}`}
                          >
                            {busyId === a.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Radio className="w-3.5 h-3.5" />
                            )}
                            Distribute
                            {distSelection.length > 0 && (
                              <span className="rounded-full bg-white/25 px-1.5 py-0.5 text-[10px] tabular-nums">
                                {distSelection.length}
                              </span>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-4 text-xs text-slate-500">
        Showing <span data-testid="publisher-admin-count">{filtered.length}</span> of {articles?.length || 0} articles.
      </div>
    </div>
  );
}
